import React from 'react';
import styled from '@benzinga/themetron';

export type BenzingaProLogoVariant = 'default' | 'light' | 'dark' | 'with-arrow' | 'blue-accent-with-arrow' | 'blue';

export interface BenzingaProLogoProps {
  className?: string;
  style?: React.CSSProperties;
  variant?: BenzingaProLogoVariant;
}

interface LogoProps {
  className?: string;
  color: string;
  style?: React.CSSProperties;
  variant?: BenzingaProLogoVariant;
}

export const BenzingaProLogo: React.FC<BenzingaProLogoProps> = ({ className, style, variant = 'default' }) => {
  if (variant === 'default') return <Logo className={className} color="#16344F" style={style} variant={variant} />;
  if (variant === 'light') return <Logo className={className} color="#FFFFFF" style={style} variant={variant} />;
  if (variant === 'dark') return <Logo className={className} color="#16344F" style={style} variant={variant} />;
  if (variant === 'with-arrow' || variant === 'blue-accent-with-arrow') {
    return <Logo className={className} color="#FFFFFF" style={style} variant={variant} />;
  }
  return <Logo className={className} color="#FFFFFF" style={style} variant={variant} />;
};

const Logo: React.FC<LogoProps> = ({ className = '', color, style, variant }) => {
  const proTextColor =
    variant === 'default' || (variant && ['blue-accent-with-arrow', 'blue'].includes(variant)) ? '#1A79FF' : '#FFFFFF';
  const hasArrow = variant === 'with-arrow' || variant === 'blue-accent-with-arrow';
  const viewBoxWidth = hasArrow ? 135 : 115;

  return (
    <IconWrapper className={`${className} ${variant}`} style={style}>
      <svg
        clipRule="evenodd"
        fillRule="evenodd"
        height="100%"
        strokeLinejoin="round"
        strokeMiterlimit="2"
        viewBox={`0 0 ${viewBoxWidth} 13`}
        width="100%"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12.654 12.94h8.449v-2.228h-5.888V7.978h4.878V5.751h-4.878V3.223h5.925V.996h-8.486V12.94zm18.617-4.42L25.738.996h-3.813l1.197 1.516v10.465h2.542V5.133l5.72 7.806h2.448V.996H31.27V8.52h.001zM8.804 6.8c1.121-.206 2.056-1.254 2.056-2.733 0-1.591-1.16-3.07-3.458-3.07H0l1.159 1.479v10.483h6.505c2.299 0 3.495-1.441 3.495-3.239-.056-1.478-1.066-2.751-2.355-2.92zM3.7 3.242h3.196c.86 0 1.402.506 1.402 1.254 0 .75-.542 1.255-1.402 1.255H3.701V3.242H3.7zm3.309 7.488H3.701V7.978h3.308c.972 0 1.514.618 1.514 1.367 0 .861-.58 1.367-1.514 1.385zm64.168-2.209h1.327v1.404a4.017 4.017 0 01-2.449.899c-2.13 0-3.626-1.629-3.626-3.819s1.514-3.82 3.626-3.82c1.103 0 2.13.581 2.729 1.517l2.019-1.123C73.944 2.212 72.43.996 70.037.996c-3.383 0-6.187 2.34-6.187 5.972 0 3.631 2.767 5.971 6.187 5.971a6.377 6.377 0 004.879-2.152V6.368h-5.458l1.72 2.153h-.001zm-11.496 0L54.131.996h-3.795l1.178 1.516v10.465h2.56V5.133l5.72 7.806h2.43V.996h-2.542V8.52l-.001.001zM83.365.996h-4.916l1.177 1.516-.205.58-3.814 9.885h2.879l.748-2.022h5.14l.748 2.022H88L83.365.996zm-3.477 7.713l1.869-5.149 1.87 5.149h-3.74.001zM46.299.996h2.56v12H46.3v-12h-.001zm-2.037 2.059V.995H35.27v2.228h5.645L35.27 10.9v2.04h9.084v-2.227H38.58l5.683-7.657-.001-.001z"
          fill={color}
          fillRule="nonzero"
        ></path>
        <path
          d="M92 12.796V3h4.26c.1 0 .23.005.387.014.157.004.303.018.437.04.596.087 1.088.275 1.474.565.392.29.68.658.867 1.102.19.44.286.93.286 1.47 0 .535-.095 1.024-.286 1.469-.191.44-.482.805-.874 1.095-.386.29-.876.479-1.467.565a7.078 7.078 0 01-.823.054h-2.535v3.422H92zm1.726-4.952h2.463c.095 0 .202-.005.322-.014.12-.01.229-.027.33-.054.285-.069.51-.189.672-.361.162-.164.283-.365.351-.585.07-.206.106-.422.107-.64a2.044 2.044 0 00-.107-.639 1.433 1.433 0 00-.35-.592c-.163-.172-.388-.292-.674-.36-.1-.028-.21-.043-.33-.048a4.307 4.307 0 00-.321-.014h-2.463v3.307zm7.42 4.952V5.449h1.525v1.79l-.186-.232c.093-.24.22-.465.379-.667.16-.202.357-.373.58-.503.191-.123.401-.218.63-.286.232-.073.473-.116.716-.13.244-.017.48-.008.709.028v1.53a2.316 2.316 0 00-.795-.06 1.884 1.884 0 00-.802.237 1.787 1.787 0 00-.587.47c-.148.19-.258.408-.329.653-.072.24-.108.501-.108.782v3.735h-1.732zm8.995.204c-.773 0-1.449-.165-2.026-.497a3.485 3.485 0 01-1.347-1.367c-.315-.585-.472-1.256-.472-2.014 0-.77.162-1.446.487-2.027a3.489 3.489 0 011.353-1.36c.578-.327 1.246-.49 2.005-.49.778 0 1.456.165 2.033.497.578.33 1.026.789 1.346 1.374.32.58.48 1.25.48 2.006 0 .762-.162 1.436-.487 2.02a3.485 3.485 0 01-1.346 1.368c-.578.327-1.253.49-2.026.49zm0-1.537c.687 0 1.198-.218 1.532-.653.334-.436.501-.998.501-1.688 0-.712-.169-1.278-.508-1.7-.339-.427-.847-.64-1.525-.64-.463 0-.845.1-1.146.3-.299.2-.53.487-.659.823-.143.349-.214.755-.214 1.217 0 .712.169 1.282.508 1.708.344.422.847.633 1.511.633z"
          fill={proTextColor}
          fillRule="nonzero"
        ></path>
        {hasArrow && (
          <path
            d="M21.188 9.281L19.78 10.72 24.063 15H4v2h20.063l-4.282 4.281 1.407 1.438L27.905 16l-6.717-6.719z"
            fill="#fff"
            fillRule="nonzero"
            transform="matrix(.5 0 0 .5 127 6.997) translate(-16 -16)"
          ></path>
        )}
      </svg>
    </IconWrapper>
  );
};

const IconWrapper = styled.span`
  display: inline-block;

  img,
  svg {
    width: 100%;
    height: 100%;
  }
`;
